import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const BOOKEO_SRC = "https://bookeo.com/widget.js?a=41569PMP4NA1862D957032";
const BOOKEO_POSITION_ID = "bookeo_position";

type BookeoWindow = Window &
  typeof globalThis & {
    bookeo_start?: () => void;
    axiomct_project?: unknown | null;
    axiomct_socket?: { destroy?: () => void } | null;
    axiomct_div?: HTMLElement | null;
    axiomct_iframe?: HTMLIFrameElement | null;
    axiomct_spinner?: HTMLElement | null;
    axiomct_loadStarted?: boolean | null;
    axiomct_initTime?: Date | null;
  };

function removeStrayBookeoNodes(keep?: HTMLElement | null) {
  ["axiomct_div", "axiomct_iframe", "axiomct_spinner", BOOKEO_POSITION_ID].forEach(
    (id) => {
      document.querySelectorAll(`#${id}`).forEach((node) => {
        if (node !== keep && !keep?.contains(node)) {
          node.parentElement?.removeChild(node);
        }
      });
    },
  );
}

function resetBookeo(el?: HTMLElement | null) {
  const w = window as BookeoWindow;
  try {
    w.axiomct_socket?.destroy?.();
  } catch {
    /* noop */
  }
  if (el) el.innerHTML = "";
  removeStrayBookeoNodes(el);

  w.axiomct_project = null;
  w.axiomct_socket = null;
  w.axiomct_div = null;
  w.axiomct_iframe = null;
  w.axiomct_spinner = null;
  w.axiomct_loadStarted = null;
  w.axiomct_initTime = null;
}

function loadAndStartBookeo(el: HTMLElement) {
  resetBookeo(el);
  const startWidget = () => {
    const w = window as BookeoWindow;
    if (typeof w.bookeo_start === "function") {
      try {
        w.bookeo_start();
      } catch {
        /* noop */
      }
    }
  };

  const existing = document.querySelector<HTMLScriptElement>(
    'script[src*="bookeo.com/widget.js"]',
  );
  if (existing) {
    startWidget();
    return;
  }

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.src = BOOKEO_SRC;
  script.async = true;
  script.dataset.bookeoManaged = "true";
  script.onload = () => startWidget();
  document.body.appendChild(script);
}

export default function BookeoToursSection() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const hasLoadedRef = useRef(false);

  // Mount the container on first open and load the widget once.
  useEffect(() => {
    if (!isOpen) return;
    if (!hasOpened) {
      setHasOpened(true);
      return;
    }
    if (hasLoadedRef.current) return;
    const el = containerRef.current;
    if (!el) return;
    hasLoadedRef.current = true;
    const timer = window.setTimeout(() => loadAndStartBookeo(el), 50);
    return () => window.clearTimeout(timer);
  }, [isOpen, hasOpened]);

  // Full cleanup only on unmount (e.g. navigating away).
  useEffect(() => {
    return () => {
      resetBookeo(containerRef.current);
      hasLoadedRef.current = false;
    };
  }, []);

  return (
    <section className="border-t border-foreground/10 bg-background px-4 sm:px-6 pb-16 sm:pb-20">
      <div className="max-w-2xl lg:max-w-6xl mx-auto">
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          aria-expanded={isOpen}
          aria-controls={BOOKEO_POSITION_ID}
          className="sticky top-[calc(env(safe-area-inset-top)+4rem)] z-[80] w-full flex flex-col items-center gap-4 bg-background/95 backdrop-blur-sm border-b border-foreground/10 px-2 sm:px-4 py-5 text-center shadow-sm pointer-events-auto"
        >
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-3">
              Guided Tours
            </div>
            <h3 className="font-serif text-3xl sm:text-4xl text-foreground">
              Book a Tour
            </h3>
          </div>
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-background bg-gold border border-gold rounded-full px-6 py-2.5 shadow-sm hover:bg-gold/90 transition">
            {isOpen ? "Hide Tours" : "Show Tours"}
            {isOpen ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </span>
        </button>


        {hasOpened && (
          <div
            ref={containerRef}
            id={BOOKEO_POSITION_ID}
            style={isOpen ? undefined : { height: 0, overflow: "hidden", visibility: "hidden" }}
            className="bookeo-widget relative z-0 mt-6 rounded-xl sm:border sm:border-foreground/10 bg-background p-0 sm:p-4 w-full flex justify-center"
            aria-hidden={!isOpen}
          />
        )}
      </div>
    </section>
  );
}

